import { Alert, Box, Group, Progress, Stack, Text } from '@mantine/core';
import { TenantColumn } from './FlagMatrixGrid';
import { CellState, DISTRIBUTION_ORDER, STATE_META, StateCounts } from './flagMatrixTypes';

export function DistributionBar({ counts, total }: { counts: StateCounts; total: number }) {
  if (total === 0) return null;
  return (
    <Progress.Root size="lg" radius="sm">
      {DISTRIBUTION_ORDER.map(state =>
        counts[state] > 0 ? (
          <Progress.Section
            key={state}
            value={(counts[state] / total) * 100}
            color={STATE_META[state].colour}
          />
        ) : null,
      )}
    </Progress.Root>
  );
}

const LEGEND_STATES: CellState[] = [
  'on_default_true',
  'on_default_false',
  'targeted_on',
  'targeted_off',
  'off',
];

export function StateLegend({ showFailed }: { showFailed: boolean }) {
  const states = showFailed ? [...LEGEND_STATES, 'failed' as const] : LEGEND_STATES;
  return (
    <Group gap="md">
      {states.map(state => (
        <Group key={state} gap={6} wrap="nowrap">
          <Box
            w={13}
            h={13}
            bg={STATE_META[state].colour}
            style={{ borderRadius: 3, flex: 'none' }}
          />
          <Text size="xs" c="dimmed">
            {STATE_META[state].label}
          </Text>
        </Group>
      ))}
    </Group>
  );
}

function groupByReason(tenants: TenantColumn[]): [string, string[]][] {
  const grouped = new Map<string, string[]>();
  tenants.forEach(tenant => {
    if (tenant.error === null) return;
    grouped.set(tenant.error, [...(grouped.get(tenant.error) ?? []), tenant.tenantName]);
  });
  return [...grouped.entries()];
}

export function TenantFailureAlert({ tenants }: { tenants: TenantColumn[] }) {
  const failures = groupByReason(tenants);
  if (failures.length === 0) return null;

  const failedCount = failures.reduce((total, [, names]) => total + names.length, 0);
  return (
    <Alert
      color={STATE_META.failed.colour.split('.')[0]}
      variant="light"
      title={`${failedCount} of ${tenants.length} tenants failed to evaluate`}
    >
      <Stack gap={4}>
        {failures.map(([reason, names]) => (
          <Text key={reason} size="xs">
            {reason}:{' '}
            <Text span ff="monospace">
              {names.join(', ')}
            </Text>
          </Text>
        ))}
      </Stack>
    </Alert>
  );
}
